import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type PositionTeam, positionTeamsQuery } from '@atlassian/search-client/positionTeam';
import { fetchOptionsAcrossSites } from '@atlassian/search-common/fetch-options-across-sites';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { generateQueryParamsGetState } from './utils';

// Number of results returned in dropdown
const LIMIT = 20;

const mapPositionTeamResult = (
	positionTeam: PositionTeam,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	return {
		trackingKey: positionTeam.node.ari || '',
		label: positionTeam.node.value?.displayName || '',
		value: positionTeam.node.ari || '',
		queryParamValue: encodeURIComponent(positionTeam.node.ari) || '',
		product: product,
		avatarUrl: positionTeam.node.value?.smallAvatarImageUrl || '',
	};
};

/**
 * Queries position teams by search term
 * @param query
 * @param config
 * @returns
 */
const fetchPositionTeamsForSite = async (
	rqlQuery: string,
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionTeamsResponse = await positionTeamsQuery(
		cloudId,
		LIMIT,
		rqlQuery,
		undefined,
		headers,
	);
	const positionTeams: PositionTeam[] = positionTeamsResponse.data?.radar_fieldValues.edges ?? [];

	return positionTeams.map((positionTeam) =>
		mapPositionTeamResult(positionTeam, config.product as ProductKeys),
	);
};

const fetchPositionTeams = async (
	rqlQuery: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	return fetchOptionsAcrossSites(config, (cloudId) =>
		fetchPositionTeamsForSite(rqlQuery, config, cloudId),
	);
};

const searchPositionTeams = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `team ~ '${query}'`;
	return fetchPositionTeams(rqlQuery, config);
};

/**
 * Queries all selected position teams by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `team in ('${queryParams
		.map((param) => decodeURIComponent(param))
		.join("', '")}')`;
	return fetchPositionTeams(rqlQuery, config);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter: Readonly<{
	id: 'positionTeam';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'positionTeam'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionTeam'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionTeam'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'positionTeam',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionTeams',
		fetcher: getOptionsWithSelectionsFromQueryParams,
		generateQueryParamsGetState,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionTeams('', config);
		},
	},
	lookup: {
		fetcher: searchPositionTeams,
	},
});
